// src/context/notifyStore.js
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const NotifyContext = createContext(null);

export function NotifyProvider({ children }) {
  // badges por tipo: mensagens, agenda, etc.
  const [badges, setBadges] = useState({
    mensagens: 0,
    agenda: 0,
  });

  // avisos globais (usados pelo Sidebar)
  // estrutura típica: { id, tipo, ts, payload, lido, cta? }
  const [avisos, setAvisos] = useState([]);

  // incrementa um badge (ex.: incBadge("agenda"))
  const incBadge = useCallback((key) => {
    setBadges((prev) => ({
      ...prev,
      [key]: (prev[key] || 0) + 1,
    }));
  }, []);

  // seta valor de um badge
  // aceita número direto OU função (prev => next)
  const setBadge = useCallback((key, value) => {
    setBadges((prev) => {
      const current = prev[key] || 0;
      const raw =
        typeof value === "function" ? value(current) : value;
      const val = Math.max(0, Number(raw) || 0);
      return {
        ...prev,
        [key]: val,
      };
    });
  }, []);

  // zera um badge (usado pelo Sidebar: clearBadge("mensagens"), etc.)
  const clearBadge = useCallback((key) => {
    setBadges((prev) => ({
      ...prev,
      [key]: 0,
    }));
  }, []);

  // adiciona um aviso (usado hoje pelo SSE, e será usado pelo Socket)
  const pushAviso = useCallback((aviso) => {
    setAvisos((prev) => {
      const id = aviso.id || `aviso-${Date.now()}-${prev.length}`;
      // evita duplicado por id
      if (prev.some((a) => a.id === id)) return prev;
      return [{ ...aviso, id }, ...prev];
    });
  }, []);

  // marca um aviso como lido (Sidebar usa para limpar cards)
  const markAvisoAsRead = useCallback((id) => {
    setAvisos((prev) =>
      prev.map((a) => (a.id === id ? { ...a, lido: true } : a))
    );
  }, []);

  const value = useMemo(
    () => ({
      // expõe badges individuais como propriedades diretas
      ...badges, // mensagens, agenda, etc.
      badges,
      avisos,
      incBadge,
      setBadge,
      clearBadge,
      pushAviso,
      addAviso: pushAviso, // alias mais semântico pro listener
      markAvisoAsRead,
    }),
    [badges, avisos, incBadge, setBadge, clearBadge, pushAviso, markAvisoAsRead]
  );

  return (
    <NotifyContext.Provider value={value}>
      {children}
    </NotifyContext.Provider>
  );
}

export function useNotify() {
  const ctx = useContext(NotifyContext);
  if (!ctx) {
    throw new Error("useNotify deve ser usado dentro de <NotifyProvider>.");
  }
  return ctx;
}
